import { FormEvent, useRef } from 'react';
import clsx from 'clsx';

import { InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

type FormProps = {
  children: JSX.Element | JSX.Element[];
  style?: {};
  className?: string;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  customValidation?: () => boolean;
};

type FormRowProps = {
  margin?: 'normal' | 'medium' | 'large';
  className?: string;
  children: JSX.Element | JSX.Element[];
};

type FormLabelProps = {
  forElement?: string;
  label: string;
  margin?: 'none' | 'normal';
};

const useStyles = makeStyles(
  (theme) => {
    return {
      form: {
        display: 'block',
        width: 400,
      },

      row: {
        margin: '12px 0',
        display: 'block',

        '&$marginMedium': {
          marginBottom: 24,
        },

        '&$marginLarge': {
          marginBottom: 36,
        },
      },

      inputLabel: {
        position: 'relative',
        transform: 'none',
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        marginBottom: 16,

        '&$noMargin': {
          marginBottom: 0,
        },
      },

      marginLarge: {},
      marginMedium: {},
      noMargin: {},
    };
  },
  {
    name: 'OMQForm',
  },
);

export const Form = ({
  children,
  style,
  className,
  onSubmit,
  customValidation,
}: FormProps): JSX.Element => {
  const classes = useStyles();

  // reference for form DOM element
  const formRef = useRef<HTMLFormElement | null>(null);

  // handle submit & validation
  // before calling callback
  const handleSubmit = (evt: FormEvent<HTMLFormElement>) => {
    const form = formRef.current;

    // prevent default submit
    evt.preventDefault();

    if (form == null || onSubmit == null) {
      return;
    }

    // check if form is valid
    const isFormValid = customValidation != null ? customValidation() : form.checkValidity();

    // call callback if everything is valid
    if (isFormValid) {
      onSubmit(evt);
    }
  };

  return (
    <form
      ref={formRef}
      className={clsx(classes.form, className)}
      style={style}
      noValidate={true}
      onSubmit={handleSubmit}>
      {children}
    </form>
  );
};

export const FormRow = ({ margin = 'normal', className, children }: FormRowProps): JSX.Element => {
  const classes = useStyles();
  return (
    <div
      className={clsx(classes.row, className, {
        [classes.marginMedium]: margin === 'medium',
        [classes.marginLarge]: margin === 'large',
      })}>
      {children}
    </div>
  );
};

export const FormLabel = ({
  forElement,
  label,
  margin = 'normal',
}: FormLabelProps): JSX.Element => {
  const classes = useStyles();
  return (
    <InputLabel
      classes={{
        root: clsx(classes.inputLabel, {
          [classes.noMargin]: margin === 'none',
        }),
      }}
      htmlFor={forElement}
      disableAnimation={true}
      variant="standard"
      shrink={false}>
      {label}
    </InputLabel>
  );
};
